
import React from 'react';
import { Helmet } from 'react-helmet';

interface BookingSEOProps {
  studioName?: string;
  studioLocation?: string;
  serviceName?: string;
}

/**
 * Specialized SEO component for booking pages
 */
export const BookingSEO: React.FC<BookingSEOProps> = ({ 
  studioName, 
  studioLocation, 
  serviceName 
}) => {
  // Generate dynamic title and description based on available data
  const title = studioName 
    ? `Termine buchen bei ${studioName}${serviceName ? ` für ${serviceName}` : ''}`
    : "Termine online buchen bei CHRONA";
  
  const description = studioName 
    ? `Buche jetzt deine Termine bei ${studioName}${studioLocation ? ` in ${studioLocation}` : ''}${serviceName ? ` für ${serviceName}` : ''} einfach online über CHRONA.`
    : "Buche Termine bei deinem Lieblingsstudio einfach online über CHRONA - spare Zeit und plane deinen nächsten Termin bequem von unterwegs.";

  // Create structured data for LocalBusiness if we have studio info
  const structuredData = studioName ? {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `${serviceName || 'Terminbuchung'} bei ${studioName}`,
    "provider": {
      "@type": "LocalBusiness",
      "name": studioName,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": studioLocation || "Deutschland"
      }
    },
    "serviceType": "Terminbuchung"
  } : null;

  const jsonLd = structuredData ? JSON.stringify(structuredData) : '';

  return (
    <Helmet>
      <title>{title} | CHRONA</title>
      <meta name="description" content={description} />
      <meta name="robots" content="noindex, follow" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={`${title} | CHRONA`} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content="https://www.chrona.it/booking" />
      <meta property="og:image" content="https://www.chrona.it/og-booking.jpg" />
      <meta property="og:site_name" content="CHRONA – Dein Termin. Deine Zeit." />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@chrona_app" />
      <meta name="twitter:title" content={`${title} | CHRONA`} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content="https://www.chrona.it/og-booking.jpg" />
      
      {/* Structured data for booking */}
      {structuredData && <script type="application/ld+json">{jsonLd}</script>}
    </Helmet>
  );
};
